// =============================================================================
// SIREPPAR - Dashboard Principal
// =============================================================================

import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, convocatoriaService, documentoService } from '../../services/api';
import {
  InboxStackIcon, BuildingLibraryIcon, DocumentTextIcon,
  ChatBubbleLeftRightIcon, BellAlertIcon, ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AlertasCardPartido, AlertasCardInterna } from '../../components/dashboard/AlertasCard';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';
import { useAlianzaPartido } from '../../hooks/useAlianzaPartido';
import BannersAlianzas from '../../components/dashboard/BannerAlianza';
import { ANIOS_CUBIERTOS } from '@utils/feriados';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import { TIPOS_CANDIDATURA, tipoDocLabels } from '../../utils/tiposDocumento';

const ROLES_CON_ALERTAS = ['APODERADO_FIRMANTE', 'PRESIDENTE_FIRMANTE', 'VICEPRESIDENTE_FIRMANTE'];

// Accesos rápidos del panel interno. Se declaran como datos y no como JSX suelto
// para que agregar uno sea una línea y todos queden con el mismo peso visual.
const ACCESOS_INTERNOS = [
  {
    href: '/gestion',
    titulo: 'Bandeja de Trabajo',
    detalle: 'Expedientes que esperan una acción tuya',
    Icono: InboxStackIcon,
    color: 'text-blue-700 bg-blue-50 group-hover:bg-blue-100 ring-blue-200',
  },
  {
    href: '/partidos',
    titulo: 'Partidos',
    detalle: 'Listado, cumplimiento del 4‰ y notas',
    Icono: BuildingLibraryIcon,
    color: 'text-emerald-700 bg-emerald-50 group-hover:bg-emerald-100 ring-emerald-200',
  },
  {
    href: '/documentos',
    titulo: 'Documentación',
    detalle: 'Verificar documentos y ver el resumen',
    Icono: DocumentTextIcon,
    color: 'text-violet-700 bg-violet-50 group-hover:bg-violet-100 ring-violet-200',
  },
  {
    href: '/interacciones',
    titulo: 'Registrar Interacción',
    detalle: 'Dejar asentado un contacto con el partido',
    Icono: ChatBubbleLeftRightIcon,
    color: 'text-amber-700 bg-amber-50 group-hover:bg-amber-100 ring-amber-200',
  },
];

// Las alertas internas arrancan OCULTAS: ocupaban toda la primera plana y
// empujaban los accesos rápidos, que es lo que se usa todos los días. Quedan a un
// clic, y la preferencia se recuerda para no tener que abrirlas en cada visita.
const CLAVE_ALERTAS_VISIBLES = 'sireppar:dashboard:alertas-visibles';
// Roles externos que pueden administrar los usuarios de su partido (acceden a /usuarios).

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const recon = useReconocimientoApoderado();
  const alianza = useAlianzaPartido();

  const isInternal = ['MESA_ENTRADAS', 'ANALISTA', 'SUPERVISOR', 'AUTORIDAD_FIRMANTE', 'ADMINISTRADOR', 'AUDITOR'].includes(user?.rol || '');
  const [alertasVisibles, setAlertasVisibles] = useState(
    () => localStorage.getItem(CLAVE_ALERTAS_VISIBLES) === '1',
  );
  const alternarAlertas = () => {
    setAlertasVisibles(v => {
      localStorage.setItem(CLAVE_ALERTAS_VISIBLES, v ? '0' : '1');
      return !v;
    });
  };
  const anioSiguiente = new Date().getFullYear() + 1
  const mostrarAlertaFeriados = isInternal && new Date().getMonth() === 11 && !ANIOS_CUBIERTOS.includes(anioSiguiente)
  
  const { data: miExpediente } = useQuery({
    queryKey: ['mi-expediente'],
    queryFn: async () => {
      const response = await api.get('/expedientes/mi-expediente');
      return response.data.data;
    },
    enabled: !isInternal,
  });

  // ¿Hay alertas del partido? (observaciones/intimaciones/plazos). Con alertas,
  // el cartel de estado va compacto arriba y las alertas en grande abajo.
  const puedeVerAlertas = !isInternal && ROLES_CON_ALERTAS.includes(user?.rol || '');
  const { data: alertasResp } = useQuery({
    queryKey: ['dashboard-alertas'],
    queryFn: async () => (await api.get('/dashboard/alertas')).data.data as { total: number },
    enabled: puedeVerAlertas,
  });
  const tieneAlertas = (alertasResp?.total ?? 0) > 0;

  // Cantidad exacta de afiliados que el partido tiene que reunir (4‰ del padrón
  // que le corresponde según su tipo y localidad, con piso de 25). Lo calcula el
  // backend: no se recalcula acá para que el número del cartel sea el mismo que el
  // de /afiliados y el del expediente.
  const { data: resumenAfiliados } = useQuery({
    queryKey: ['afiliados-resumen-dashboard', user?.partidoId],
    queryFn: async () => (await api.get(`/afiliados/partido/${user!.partidoId}/resumen`)).data as {
      minimoRequerido: number; afiliadosValidos: number;
    },
    enabled: !isInternal && !!user?.partidoId && recon.enEtapaAfiliados,
  });

  const numeroExp: string | undefined = recon.miExpediente?.numero || miExpediente?.numero;

  // Cartel de "agrupación habilitada para presentar candidatos" (Candidaturas Gaiman).
  // Independiente del sistema de carteles de reconocimiento de arriba — una
  // agrupación puede estar en cualquier estado de reconocimiento y a la vez estar
  // habilitada para esta convocatoria puntual, así que no son mutuamente excluyentes.
  const { data: estadoCandidatura } = useQuery({
    queryKey: ['dashboard-candidatura-estado'],
    queryFn: async () => (await convocatoriaService.getMiEstado()).data.data,
    enabled: !isInternal && user?.rol === 'APODERADO_FIRMANTE',
    retry: false,
  });
  const cierreCandidaturas: string | undefined = estadoCandidatura?.convocatoria?.plazos?.cierre_candidaturas;
  const plazoCandidaturasVencido = (() => {
    if (!cierreCandidaturas) return false;
    const [anio, mes, dia] = cierreCandidaturas.slice(0, 10).split('-').map(Number);
    const finDelCierre = new Date(anio, mes - 1, dia, 23, 59, 59, 999);
    return new Date() > finDelCierre;
  })();
  const claveFelicitacionCandidatura = estadoCandidatura?.habilitada && estadoCandidatura?.convocatoria?.id
  ? `sireppar:felicitacion-candidatura:${estadoCandidatura.convocatoria.id}`
  : null;
  const [felicitacionCandidaturaCerrada, setFelicitacionCandidaturaCerrada] = useState(false);
  useEffect(() => {
    if (!claveFelicitacionCandidatura) return;
    setFelicitacionCandidaturaCerrada(localStorage.getItem(claveFelicitacionCandidatura) === '1');
  }, [claveFelicitacionCandidatura]);
  const cerrarFelicitacionCandidatura = () => {
    if (claveFelicitacionCandidatura) localStorage.setItem(claveFelicitacionCandidatura, '1');
    setFelicitacionCandidaturaCerrada(true);
  };
  const mostrarCartelCandidatura = Boolean(estadoCandidatura?.habilitada && !plazoCandidaturasVencido && claveFelicitacionCandidatura && !felicitacionCandidaturaCerrada);
  // Resolución de la alianza que el usuario administra, para adjuntarla al cartel de
  // felicitaciones (ver el bloque de abajo).
  const resolucionAlianza = alianza.alianzaPropia?.resolucion ?? null;

  // Cartel de felicitaciones de la ALIANZA oficializada por el TEP.
  //
  // Hace falta uno propio porque el de candidatura NO se dispara con esto: depende
  // de `estadoCandidatura.habilitada`, que sale de una fila que alguien de adentro
  // marca a mano para la convocatoria, no de que la alianza haya quedado VIGENTE.
  // Sin este cartel, la alianza recién oficializada no tenía ningún acuse en el
  // inicio y el apoderado se enteraba sólo por el correo.
  //
  // Clave de cierre PROPIA (ni la de reconocimiento ni la de reempadronamiento):
  // son trámites distintos y cerrar uno no puede ocultar los otros.
  const claveFelicitacionAlianza = alianza.alianzaPropia
    ? `sireppar:felicitacion-alianza:${alianza.alianzaPropia.alianzaId}`
    : null;
  const [felicitacionAlianzaCerrada, setFelicitacionAlianzaCerrada] = useState(false);
  useEffect(() => {
    if (!claveFelicitacionAlianza) return;
    setFelicitacionAlianzaCerrada(localStorage.getItem(claveFelicitacionAlianza) === '1');
  }, [claveFelicitacionAlianza]);
  const cerrarFelicitacionAlianza = () => {
    if (claveFelicitacionAlianza) localStorage.setItem(claveFelicitacionAlianza, '1');
    setFelicitacionAlianzaCerrada(true);
  };
  // Se OCULTA cuando está el de candidatura: dice lo mismo (la alianza puede
  // presentar candidatos) y además trae la misma descarga de la resolución. Un solo
  // cartel por vez.
  const mostrarCartelAlianza = Boolean(
    alianza.alianzaPropiaVigente
    && claveFelicitacionAlianza
    && !felicitacionAlianzaCerrada
    && !mostrarCartelCandidatura,
  );

  // El cartel de felicitaciones se puede cerrar: la fase RECONOCIMIENTO_DEFINITIVO
  // es definitiva, así que sin esto quedaría fijo en el inicio para siempre. Se
  // recuerda por expediente en localStorage (no amerita guardarlo en la base).
  const claveFelicitacion = recon.expedienteId ? `sireppar:felicitacion:${recon.expedienteId}` : null;
  const [felicitacionCerrada, setFelicitacionCerrada] = useState(false);
  useEffect(() => {
    if (!claveFelicitacion) return;
    setFelicitacionCerrada(localStorage.getItem(claveFelicitacion) === '1');
  }, [claveFelicitacion]);
  const cerrarFelicitacion = () => {
    if (claveFelicitacion) localStorage.setItem(claveFelicitacion, '1');
    setFelicitacionCerrada(true);
  };

  // Cartel de felicitaciones del REEMPADRONAMIENTO aprobado. Clave propia (y no la
  // de reconocimiento) para que cerrar uno no oculte el otro: son trámites
  // distintos y un mismo partido puede pasar por los dos.
  const claveFelicitacionReemp = recon.expedienteId
    ? `sireppar:felicitacion-reemp:${recon.expedienteId}`
    : null;
  const [felicitacionReempCerrada, setFelicitacionReempCerrada] = useState(false);
  useEffect(() => {
    if (!claveFelicitacionReemp) return;
    setFelicitacionReempCerrada(localStorage.getItem(claveFelicitacionReemp) === '1');
  }, [claveFelicitacionReemp]);
  const cerrarFelicitacionReemp = () => {
    if (claveFelicitacionReemp) localStorage.setItem(claveFelicitacionReemp, '1');
    setFelicitacionReempCerrada(true);
  };
  // La constitución de alianza queda AFUERA de este cartel genérico: tiene su
  // propio banner (BannersAlianzas), que además dice qué se espera en cada estado.
  // Sin esta exclusión, al usuario de la alianza le aparecía "Reempadronamiento en
  // revisión" y el early return de abajo le tapaba el banner de su alianza.
  const expedienteEnRevision = miExpediente
    && miExpediente.tipo !== 'CONSTITUCION_ALIANZA'
    && ['PRESENTADO', 'EN_ADMISIBILIDAD'].includes(miExpediente.estado);

  // Cartel de estado del partido externo, en versión grande y compacta.
  let cartelGrande: ReactNode = null;
  let cartelCompacto: ReactNode = null;
  let siempreCompacto = false; // documentación enviada → siempre compacto + alertas

  // Etapa de afiliados del reconocimiento provisorio: es LA tarea del partido en
  // este tramo (4 meses para reunir el 4‰), así que va en primera plana igual que
  // el wizard de documentación, con los días restantes bien visibles.
  // Días que le quedan del plazo de 4 meses del provisorio. Es UNO SOLO para las
  // dos etapas (afiliados y autoridades definitivas): corre desde la resolución del
  // TEP que otorgó el provisorio, y el backend lo persiste en el expediente.
  const venceProvisorio = recon.miExpediente?.plazoAfiliadosVence;
  const diasRestantesProvisorio = venceProvisorio
    ? Math.round(
        (new Date(new Date(venceProvisorio).setHours(0, 0, 0, 0)).getTime() -
          new Date(new Date().setHours(0, 0, 0, 0)).getTime()) / 86400000,
      )
    : null;
  const plazoVencido = diasRestantesProvisorio !== null && diasRestantesProvisorio < 0;
  const textoPlazoProvisorio = diasRestantesProvisorio === null
    ? null
    : plazoVencido
      ? `El plazo venció hace ${Math.abs(diasRestantesProvisorio)} ${Math.abs(diasRestantesProvisorio) === 1 ? 'día' : 'días'}`
      : diasRestantesProvisorio === 0
        ? 'El plazo vence hoy'
        : `Te quedan ${diasRestantesProvisorio} ${diasRestantesProvisorio === 1 ? 'día' : 'días'}`;

  // Etapa de autoridades definitivas: el partido tiene que hacer sus elecciones
  // internas y cargar las autoridades electas, dentro del mismo plazo de 4 meses.
  if (recon.enEtapaAutoridades) {
    if (recon.mostrarWizardAutoridades) {
      cartelGrande = (
        <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-green-100">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Cargá las autoridades definitivas de tu partido</h2>
          <p className="text-gray-600 mb-2 text-lg">La Secretaría Electoral verificó tu <strong>padrón de afiliados</strong>.</p>
          <p className="text-gray-600 mb-6">
            Ahora tenés que <strong>convocar a elecciones internas</strong> y cargar las autoridades electas
            y tus apoderados, junto con la <strong>Publicación de llamado a elecciones</strong>, el
            <strong> Cronograma de elecciones</strong>, el <strong>Acta de asamblea donde se proclamaron
            las autoridades</strong>, la <strong>Resolución de la Proclamación de Autoridades</strong> y el
            <strong> Acta de Designación de Apoderados</strong>.
          </p>
          {textoPlazoProvisorio && (
            <div className="mb-8">
              <p className={`text-lg font-bold ${plazoVencido ? 'text-red-700' : 'text-green-700'}`}>{textoPlazoProvisorio}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Plazo de 4 meses desde el reconocimiento provisorio — vence el {new Date(venceProvisorio!).toLocaleDateString('es-AR')}
              </p>
            </div>
          )}
          <button onClick={() => navigate('/reconocimiento/autoridades')} className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
            Cargar autoridades definitivas
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
          {numeroExp && <p className="mt-6 text-sm text-gray-500">Expediente N°: <strong>{numeroExp}</strong></p>}
        </div>
      );
      cartelCompacto = (
        <div className={`bg-white shadow rounded-lg p-4 flex flex-wrap items-center justify-between gap-4 border ${plazoVencido ? 'border-red-200' : 'border-green-200'}`}>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-lg">🏛️</div>
            <div>
              <p className="font-semibold text-gray-900">Cargá las autoridades definitivas de tu partido</p>
              <p className="text-sm text-gray-500">
                Elecciones internas + autoridades y apoderados{textoPlazoProvisorio ? ` · ${textoPlazoProvisorio}` : ''}{numeroExp ? ` · Exp. ${numeroExp}` : ''}
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/reconocimiento/autoridades')} className="rounded-lg bg-green-600 hover:bg-green-700 px-5 py-2.5 text-sm font-semibold text-white">Cargar autoridades →</button>
        </div>
      );
    } else {
      siempreCompacto = true;
      cartelCompacto = (
        <div className="bg-white shadow rounded-lg border border-green-200 p-4 flex flex-wrap items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <p className="font-semibold text-gray-900">Autoridades definitivas presentadas — en revisión</p>
            <p className="text-sm text-gray-500">
              La Secretaría Electoral está revisando tu presentación{numeroExp ? ` · Exp. ${numeroExp}` : ''}. Podés ver la nómina vigente en Autoridades.
            </p>
          </div>
        </div>
      );
    }
  } else if (recon.obtuvoDefinitivo && !felicitacionCerrada) {
    // Fin del trámite: el TEP otorgó el reconocimiento definitivo. Se puede cerrar,
    // porque la fase queda en RECONOCIMIENTO_DEFINITIVO para siempre y un cartel
    // fijo terminaría siendo ruido en el inicio.
    cartelGrande = (
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-green-200">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6 text-4xl">🎉</div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">¡Felicitaciones!</h2>
        <p className="text-gray-600 mb-2 text-lg">
          El Tribunal Electoral Provincial le otorgó el <strong>reconocimiento definitivo</strong> a tu partido.
        </p>
        <p className="text-gray-600 mb-8">
          Ya está inscripto como partido político <strong>vigente</strong> en el Registro de Partidos
          Políticos de la Provincia del Chubut. Desde ahora tenés disponibles las secciones
          <strong> Mi Partido</strong> y <strong>Financiamiento</strong>, donde vas a gestionar tus
          rendiciones de cuentas y los datos del partido.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => navigate('/mi-partido')} className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
            Ir a Mi Partido
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
          <button onClick={cerrarFelicitacion} className="rounded-xl border border-gray-300 px-6 py-4 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Entendido, no volver a mostrar
          </button>
        </div>
        {numeroExp && <p className="mt-6 text-sm text-gray-500">Expediente N°: <strong>{numeroExp}</strong></p>}
      </div>
    );
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-green-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-lg">🎉</div>
          <div>
            <p className="font-semibold text-gray-900">¡Felicitaciones! Tu partido obtuvo el reconocimiento definitivo</p>
            <p className="text-sm text-gray-500">
              Ya está vigente en el Registro de Partidos Políticos{numeroExp ? ` · Exp. ${numeroExp}` : ''}.
            </p>
          </div>
        </div>
        <button onClick={cerrarFelicitacion} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Entendido
        </button>
      </div>
    );
  } else if (recon.obtuvoReempadronamiento && !felicitacionReempCerrada) {
    // Fin del trámite de reempadronamiento: el Secretario Electoral (o el TEP)
    // resolvió a favor y la agrupación quedó VIGENTE. Antes acá no había ningún
    // cartel: el partido se quedaba con el inicio vacío y sin saber que el trámite
    // había terminado bien. Se puede cerrar, igual que el de reconocimiento,
    // porque el estado VIGENTE es permanente y un cartel fijo sería ruido.
    cartelGrande = (
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-green-200">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6 text-4xl">🎉</div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">¡Felicitaciones!</h2>
        <p className="text-gray-600 mb-2 text-lg">
          Se aprobó el <strong>reempadronamiento</strong> de tu agrupación.
        </p>
        <p className="text-gray-600 mb-8">
          Queda inscripta como agrupación política <strong>vigente</strong> en el Registro de
          Partidos Políticos de la Provincia del Chubut. Desde ahora tenés disponible la sección
          <strong> Financiamiento</strong>, donde vas a gestionar tus rendiciones de cuentas.
          Podés ver la resolución en los documentos de tu expediente.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={() => navigate('/financiamiento')} className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
            Ir a Financiamiento
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
          <button onClick={cerrarFelicitacionReemp} className="rounded-xl border border-gray-300 px-6 py-4 text-sm font-medium text-gray-600 hover:bg-gray-50">
            Entendido, no volver a mostrar
          </button>
        </div>
        {numeroExp && <p className="mt-6 text-sm text-gray-500">Expediente N°: <strong>{numeroExp}</strong></p>}
      </div>
    );
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-green-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-lg">🎉</div>
          <div>
            <p className="font-semibold text-gray-900">¡Felicitaciones! Se aprobó el reempadronamiento de tu agrupación</p>
            <p className="text-sm text-gray-500">
              Ya está vigente en el Registro de Partidos Políticos{numeroExp ? ` · Exp. ${numeroExp}` : ''}.
            </p>
          </div>
        </div>
        <button onClick={cerrarFelicitacionReemp} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Entendido
        </button>
      </div>
    );
  } else if (recon.enTramiteDefinitivo) {
    // El partido ya presentó todo: el trámite está del lado interno. No hay acción
    // para él, pero tiene que ver dónde está — si no, el inicio queda vacío y
    // parece que el trámite se terminó.
    siempreCompacto = true;
    const enElTep = recon.enTepDefinitivo;
    cartelCompacto = (
      <div className={`bg-white shadow rounded-lg border p-4 flex flex-wrap items-center gap-3 ${enElTep ? 'border-cyan-200' : 'border-indigo-200'}`}>
        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-lg ${enElTep ? 'bg-cyan-100' : 'bg-indigo-100'}`}>
          {enElTep ? '⚖️' : '📋'}
        </div>
        <div>
          <p className="font-semibold text-gray-900">
            {enElTep
              ? 'Tu trámite está en el Tribunal Electoral Provincial'
              : 'Tu trámite está en la Secretaría Electoral Permanente'}
          </p>
          <p className="text-sm text-gray-500">
            {enElTep
              ? 'La Secretaría Electoral revisó tus autoridades definitivas y elevó el expediente al Tribunal, que va a resolver sobre el reconocimiento definitivo.'
              : 'Presentaste tus autoridades definitivas y la Secretaría Electoral las está revisando antes de elevarlas al Tribunal.'}
            {numeroExp ? ` · Exp. ${numeroExp}` : ''}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            No tenés ninguna acción pendiente. Te vamos a avisar por correo cuando haya novedades.
          </p>
        </div>
      </div>
    );
  } else if (recon.enEtapaAfiliados) {
    const vence = venceProvisorio;
    const diasRestantes = diasRestantesProvisorio;
    const vencido = plazoVencido;
    const textoPlazo = textoPlazoProvisorio;

    cartelGrande = (
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-blue-100">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-6">
          <svg className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Cargá los afiliados de tu partido</h2>
        <p className="text-gray-600 mb-2 text-lg">El Tribunal Electoral te otorgó el <strong>reconocimiento provisorio</strong>.</p>
        <p className="text-gray-600 mb-6">
          Para obtener el reconocimiento definitivo tenés que reunir el <strong>4‰</strong>
          {resumenAfiliados
            ? <>, es decir <strong>{resumenAfiliados.minimoRequerido.toLocaleString('es-AR')} afiliados</strong>,</>
            : ' del padrón electoral'} y
          presentar el padrón: el <strong>Excel</strong> que completaste y el mismo listado
          <strong> firmado en PDF</strong>. También podés cargar afiliados de a uno.
        </p>
        {textoPlazo && (
          <div className="mb-8">
            <p className={`text-lg font-bold ${vencido ? 'text-red-700' : 'text-blue-700'}`}>{textoPlazo}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Plazo de 4 meses — vence el {new Date(vence!).toLocaleDateString('es-AR')}
            </p>
          </div>
        )}
        <div>
          <button onClick={() => navigate('/afiliados')} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 hover:bg-blue-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
            Ir a cargar afiliados
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
        </div>
      </div>
    );
    cartelCompacto = (
      <div className={`bg-white shadow rounded-lg p-4 flex flex-wrap items-center justify-between gap-4 border ${vencido ? 'border-red-200' : 'border-blue-200'}`}>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-lg">👥</div>
          <div>
            <p className="font-semibold text-gray-900">Cargá los afiliados de tu partido</p>
            <p className="text-sm text-gray-500">
              Padrón de afiliados (Excel + PDF firmado){textoPlazo ? ` · ${textoPlazo}` : ''}{numeroExp ? ` · Exp. ${numeroExp}` : ''}
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/afiliados')} className="rounded-lg bg-blue-600 hover:bg-blue-700 px-5 py-2.5 text-sm font-semibold text-white">Cargar afiliados →</button>
      </div>
    );
  } else if (recon.mostrarWizard) {
    cartelGrande = (
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-green-100">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
          <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Presentá la documentación de tu partido</h2>
        <p className="text-gray-600 mb-2 text-lg">El Tribunal Electoral aprobó los atributos de tu partido en formación.</p>
        <p className="text-gray-600 mb-8">Ahora corresponde presentar la documentación (Art. 18) y declarar los órganos y las autoridades promotoras. Lo hacés todo en un solo asistente, paso a paso.</p>
        <button onClick={() => navigate('/reconocimiento/documentacion')} className="inline-flex items-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
          Continuar con la presentación
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </button>
        {numeroExp && <p className="mt-6 text-sm text-gray-500">Expediente N°: <strong>{numeroExp}</strong></p>}
      </div>
    );
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-green-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-lg">🏛️</div>
          <div>
            <p className="font-semibold text-gray-900">Presentá la documentación de tu partido</p>
            <p className="text-sm text-gray-500">Documentos Art. 18 + órganos y autoridades promotoras{numeroExp ? ` · Exp. ${numeroExp}` : ''}</p>
          </div>
        </div>
        <button onClick={() => navigate('/reconocimiento/documentacion')} className="rounded-lg bg-green-600 hover:bg-green-700 px-5 py-2.5 text-sm font-semibold text-white">Continuar →</button>
      </div>
    );
  } else if (recon.esReconocimientoEnFormacion && recon.fase === 'ATRIBUTOS_RECHAZADOS') {
    cartelGrande = (
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl p-10 text-center border border-red-100">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
          <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">El Tribunal Electoral rechazó los atributos</h2>
        <p className="text-gray-600 mb-2 text-lg">Debés corregir los atributos de tu partido en formación.</p>
        <p className="text-gray-600 mb-8">Al corregirlos, el trámite vuelve a la Secretaría Electoral, que revisa y vuelve a publicar los atributos con un nuevo plazo de oposición de 5 días.</p>
        <button onClick={() => navigate('/reconocimiento/corregir-atributos')} className="inline-flex items-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 px-8 py-4 text-lg font-semibold text-white shadow-md transition">
          Corregir atributos
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
        </button>
        {numeroExp && <p className="mt-6 text-sm text-gray-500">Expediente N°: <strong>{numeroExp}</strong></p>}
      </div>
    );
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-red-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 text-lg">⚠️</div>
          <div>
            <p className="font-semibold text-gray-900">El Tribunal Electoral rechazó los atributos</p>
            <p className="text-sm text-gray-500">Corregí los atributos para volver a publicarlos{numeroExp ? ` · Exp. ${numeroExp}` : ''}</p>
          </div>
        </div>
        <button onClick={() => navigate('/reconocimiento/corregir-atributos')} className="rounded-lg bg-red-600 hover:bg-red-700 px-5 py-2.5 text-sm font-semibold text-white">Corregir atributos →</button>
      </div>
    );
  } else if (recon.esReconocimientoEnFormacion && recon.documentacionCargada) {
    siempreCompacto = true;
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-green-200 p-4 flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <svg className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <div>
          <p className="font-semibold text-gray-900">Documentación enviada — en revisión</p>
          <p className="text-sm text-gray-500">La Secretaría Electoral está revisando tu presentación{numeroExp ? ` · Exp. ${numeroExp}` : ''}. Podés ver tus autoridades promotoras en Autoridades.</p>
        </div>
      </div>
    );
  } else if (recon.esReconocimientoEnFormacion || expedienteEnRevision) {
    const tipo = recon.esReconocimientoEnFormacion ? 'RECONOCIMIENTO' : (miExpediente?.tipo ?? 'RECONOCIMIENTO');
    const titulo = tipo === 'RECONOCIMIENTO' ? 'Constitución de partido en revisión' : 'Reempadronamiento en revisión';
    cartelGrande = (
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-6">
          <svg className="h-8 w-8 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{titulo}</h2>
        <p className="text-gray-600 mb-2">Su presentación fue recibida exitosamente.</p>
        {numeroExp && <p className="text-gray-600 mb-6">Expediente N°: <strong>{numeroExp} — {tipoTramiteLabel(tipo)}</strong></p>}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-800 mb-2">¿Qué sigue?</h3>
          <ul className="text-sm text-blue-700 text-left space-y-2">
            <li className="flex items-start"><span className="mr-2">1.</span><span>La Secretaría Electoral Permanente dispone de <strong>5 días hábiles</strong> para el análisis {tipo === 'RECONOCIMIENTO' ? 'de la reserva de atributos' : 'de admisibilidad'}.</span></li>
            <li className="flex items-start"><span className="mr-2">2.</span><span>Si la documentación es correcta, el expediente pasará a análisis.</span></li>
            <li className="flex items-start"><span className="mr-2">3.</span><span>Si hay documentación faltante o incorrecta, recibirá una intimación por correo electrónico y podrá responder y adjuntar documentación directamente desde el expediente en esta plataforma.</span></li>
          </ul>
        </div>
        <p className="text-sm text-gray-500">Consultas:{' '}<a href="mailto:registro@electoralchubut.gob.ar" className="text-blue-600 hover:underline">registro@electoralchubut.gob.ar</a></p>
      </div>
    );
    cartelCompacto = (
      <div className="bg-white shadow rounded-lg border border-yellow-200 p-4 flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <div>
          <p className="font-semibold text-gray-900">{titulo}</p>
          <p className="text-sm text-gray-500">Tu presentación está en análisis{numeroExp ? ` · Exp. ${numeroExp}` : ''}. Respondé las intimaciones si las hubiera.</p>
        </div>
      </div>
    );
  }

  if (cartelGrande || cartelCompacto) {
    // Con alertas (o documentación enviada, o una alianza en curso): cartel compacto
    // arriba + lo demás en grande abajo.
    //
    // Las alianzas fuerzan este camino: el cartel grande se renderiza centrado y
    // SOLO, así que con una alianza en trámite el banner quedaba tapado. Pasa de
    // verdad — un partido con el reconocimiento definitivo recién obtenido tiene el
    // cartel de felicitaciones (que se puede cerrar, pero está ahí en el primer
    // ingreso) y es justo el que puede integrar una alianza.
    if (siempreCompacto || tieneAlertas || alianza.hayAlianzas) {
      return (
        <div className="space-y-6">
          {cartelCompacto ?? cartelGrande}
          <BannersAlianzas ocultarVigenteConAcceso={mostrarCartelCandidatura} />
          {puedeVerAlertas && <AlertasCardPartido />}
        </div>
      );
    }
    // Sin alertas ni alianzas: cartel grande en primera plana.
    return <div className="min-h-[60vh] flex items-center justify-center">{cartelGrande ?? cartelCompacto}</div>;
  }

  return (
    <div className="space-y-6">
            <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">Bienvenido, {user?.nombre}</h1>
          <p className="mt-1 text-sm text-gray-500">{isInternal ? 'Panel de control del Sistema de Registro Permanente' : 'Portal de gestión de su partido político'}</p>
        </div>
      </div>

      {mostrarCartelCandidatura && (
        <div className="max-w-2xl w-full mx-auto bg-white shadow-lg rounded-2xl p-8 text-center border border-green-200">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 text-3xl">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Felicitaciones!</h2>
          <p className="text-gray-600 mb-2">
            Tu agrupación está <strong>habilitada</strong> para presentar candidatos en la{' '}
            <strong>{estadoCandidatura?.convocatoria?.denominacion}</strong>.
          </p>
          <p className="text-gray-600 mb-2">Ya podés cargar tu candidato desde la sección Candidaturas.</p>

          <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
            <p className="text-sm font-medium text-gray-900 mb-2">Documentación que vas a necesitar para el candidato:</p>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              {TIPOS_CANDIDATURA.map(tipo => (
                <li key={tipo}>{tipoDocLabels[tipo]}</li>
              ))}
            </ul>
          </div>

          {/* Resolución que la reconoció. Estaba sólo en el banner de la alianza, que
              para una alianza VIGENTE decía lo mismo que este cartel; se dejó uno y el
              archivo se movió acá. Sigue estando en el expediente. */}
          {resolucionAlianza && resolucionAlianza.archivos.length > 0 && (
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-3 text-left">
              <p className="text-sm font-medium text-green-900">
                Resolución que la reconoce
                {resolucionAlianza.numero && ` — N° ${resolucionAlianza.numero}`}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {resolucionAlianza.archivos.map(archivo => (
                  <button
                    key={archivo.id}
                    type="button"
                    onClick={() => documentoService.forceDownload(archivo.id, archivo.nombre)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {archivo.nombre}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/apoderado/panel')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-6 py-3 text-sm font-semibold text-white shadow-md transition">
              Ir a Candidaturas
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </button>
            <button onClick={cerrarFelicitacionCandidatura} className="rounded-xl border border-gray-300 px-6 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Entendido, no volver a mostrar
            </button>
          </div>
        </div>
      )}

      {/* Cartel de la ALIANZA oficializada por el TEP. Va acá abajo, junto al de
          candidatura, y NO en la cadena de carteles de `recon`: esa cadena mira el
          expediente de la agrupación activa, que para el usuario de una alianza es
          de tipo CONSTITUCION_ALIANZA y no enciende ninguna de sus banderas, así
          que el early return de arriba nunca se dispara y este bloque sí se
          renderiza. */}
      {mostrarCartelAlianza && (
        <div className="max-w-2xl w-full mx-auto bg-white shadow-lg rounded-2xl p-8 text-center border border-green-200">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 text-3xl">🎉</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Felicitaciones!</h2>
          <p className="text-gray-600 mb-2">
            El <strong>Tribunal Electoral Provincial</strong> oficializó la alianza{' '}
            <strong>{alianza.alianzaPropia?.denominacion}</strong>.
          </p>
          <p className="text-gray-600 mb-6">
            Queda habilitada para oficializar listas de candidatos. Los candidatos se presentan por la
            alianza, no por cada partido integrante.
          </p>

          {/* La resolución que la oficializó. Es lo primero que las agrupaciones
              piden después del correo, y acá la tienen sin entrar al expediente. */}
          {resolucionAlianza && resolucionAlianza.archivos.length > 0 && (
            <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-3 text-left">
              <p className="text-sm font-medium text-green-900">
                Resolución que la oficializa
                {resolucionAlianza.numero && ` — ${resolucionAlianza.numero}`}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {resolucionAlianza.archivos.map(archivo => (
                  <button
                    key={archivo.id}
                    type="button"
                    onClick={() => documentoService.forceDownload(archivo.id, archivo.nombre)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {archivo.nombre}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/apoderado/panel')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 hover:bg-green-700 px-6 py-3 text-sm font-semibold text-white shadow-md transition">
              Ir a Candidaturas
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </button>
            <button onClick={cerrarFelicitacionAlianza} className="rounded-xl border border-gray-300 px-6 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Entendido, no volver a mostrar
            </button>
          </div>
        </div>
      )}

      {/* `ocultarVigenteConAcceso` incluye los DOS carteles porque los dos traen la
          resolución: si se ocultara el banner sin que ninguno esté en pantalla, la
          alianza vigente se quedaría sin banner y sin resolución en ningún lado.
          Ojo: la lista NO incluye el early return de arriba, donde este cartel no
          se renderiza. */}
      {!isInternal && <BannersAlianzas ocultarVigenteConAcceso={mostrarCartelCandidatura || mostrarCartelAlianza} />}

      {mostrarAlertaFeriados && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
          <div className="flex items-start gap-3">
            <span className="text-yellow-600 text-lg">⚠</span>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Actualizar feriados {anioSiguiente}</h3>
              <p className="mt-1 text-sm text-yellow-700">
                El archivo <code className="bg-yellow-100 px-1 rounded">src/utils/feriados.ts</code> no tiene los feriados de {anioSiguiente}. Actualizarlo antes de fin de año con el decreto del PEN.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {isInternal && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ACCESOS_INTERNOS.map(({ href, titulo, detalle, Icono, color }) => (
              <a
                key={href}
                href={href}
                className="group bg-white rounded-xl shadow-sm ring-1 ring-gray-200 p-5 flex flex-col gap-3 transition hover:shadow-lg hover:-translate-y-0.5 hover:ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <span className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ring-1 transition-colors ${color}`}>
                  <Icono className="w-6 h-6" />
                </span>
                <span className="text-lg font-semibold text-gray-900 leading-snug">{titulo}</span>
                <span className="text-sm text-gray-500 leading-snug">{detalle}</span>
              </a>
            ))}
          </div>
        )}

      </div>

      {!isInternal && !recon.esReconocimientoEnFormacion && ROLES_CON_ALERTAS.includes(user?.rol || '') && (
        <AlertasCardPartido />
      )}

      {isInternal && (
        <div className="space-y-4">
          <button
            onClick={alternarAlertas}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
          >
            <BellAlertIcon className="w-5 h-5 text-gray-400" />
            {alertasVisibles ? 'Ocultar alertas' : 'Ver alertas'}
            <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${alertasVisibles ? 'rotate-180' : ''}`} />
          </button>
          {alertasVisibles && <AlertasCardInterna />}
        </div>
      )}
    </div>
  );
}